import { css, keyframes } from "dx-styles"
import type { FC } from "react"

const bob = keyframes({
  "0%, 100%": { transform: "translateY(3px)" },
  "50%": { transform: "translateY(-5px)" },
})

const blink = keyframes({
  "0%, 86%, 100%": { transform: "scaleY(1)" },
  "90%": { transform: "scaleY(0.08)" },
  "94%": { transform: "scaleY(1)" },
})

const swing = keyframes({
  "0%": { transform: "rotate(52deg)" },
  "45%": { transform: "rotate(100deg)" },
  "58%": { transform: "rotate(96deg)" },
  "100%": { transform: "rotate(52deg)" },
})

const thud = keyframes({
  "0%, 40%": { opacity: 0, transform: "scale(0.4)" },
  "52%": { opacity: 1, transform: "scale(1)" },
  "70%, 100%": { opacity: 0, transform: "scale(1.3)" },
})

const tailTap = keyframes({
  "0%, 100%": { transform: "rotate(0deg)" },
  "50%": { transform: "rotate(-9deg)" },
})

const orbit = keyframes({
  from: { transform: "rotate(0deg)" },
  to: { transform: "rotate(360deg)" },
})

const twinkle = keyframes({
  "0%, 100%": { opacity: 0.25 },
  "50%": { opacity: 1 },
})

const beaver = css({
  position: "relative",
  display: "block",
  width: "100%",
  height: "100%",
  overflow: "visible",
})

const float = css({
  transformBox: "fill-box",
  transformOrigin: "center",
  animation: `${bob} 3.4s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const eye = css({
  transformBox: "fill-box",
  transformOrigin: "center",
  animation: `${blink} 4.8s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const eyeEnd = css({ animationDelay: "70ms" })

const hammer = css({
  transformBox: "fill-box",
  transformOrigin: "50% 93%",
  animation: `${swing} 1.6s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    transform: "rotate(66deg)",
  },
})

const impact = css({
  transformBox: "fill-box",
  transformOrigin: "center",
  animation: `${thud} 1.6s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 0 },
})

const tail = css({
  transformBox: "fill-box",
  transformOrigin: "right center",
  animation: `${tailTap} 3.4s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const motes = css({
  transformOrigin: "110px 110px",
  animation: `${orbit} 18s linear infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const mote = css({
  animation: `${twinkle} 2.8s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    opacity: 0.6,
  },
})

const moteDelayMid = css({ animationDelay: "0.9s" })
const moteDelayLate = css({ animationDelay: "1.8s" })

export const Builder: FC = () => {
  return (
    <svg
      viewBox="0 0 220 220"
      className={beaver}
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id="agentsBeaverBody"
          x1="0"
          y1="0"
          x2="0"
          y2="1"
        >
          <stop
            offset="0%"
            stopColor="#B2794F"
          />
          <stop
            offset="55%"
            stopColor="#8A5A38"
          />
          <stop
            offset="100%"
            stopColor="#5C3A22"
          />
        </linearGradient>
        <linearGradient
          id="agentsHardHat"
          x1="0"
          y1="0"
          x2="0"
          y2="1"
        >
          <stop
            offset="0%"
            stopColor="#FFB84D"
          />
          <stop
            offset="100%"
            stopColor="#FF9903"
          />
        </linearGradient>
        <linearGradient
          id="agentsMuzzle"
          x1="0"
          y1="0"
          x2="0"
          y2="1"
        >
          <stop
            offset="0%"
            stopColor="#FFF6EC"
          />
          <stop
            offset="100%"
            stopColor="#F3DFC9"
          />
        </linearGradient>
      </defs>

      <g className={motes}>
        <circle
          className={mote}
          cx="110"
          cy="12"
          r="5"
          fill="#FF9903"
        />
        <circle
          className={`${mote} ${moteDelayMid}`}
          cx="194.9"
          cy="159"
          r="4"
          fill="#FF375B"
        />
        <circle
          className={`${mote} ${moteDelayLate}`}
          cx="25.1"
          cy="159"
          r="6"
          fill="#33C4B3"
        />
      </g>

      <g className={float}>
        <ellipse
          cx="110"
          cy="196"
          rx="48"
          ry="8"
          fill="rgba(70, 40, 83, 0.14)"
        />

        <g className={tail}>
          <ellipse
            cx="46"
            cy="170"
            rx="28"
            ry="15"
            transform="rotate(-18 46 170)"
            fill="#6B4630"
          />
          <path
            d="M28 166 L60 174 M32 158 L62 166 M26 175 L56 182"
            stroke="rgba(255, 255, 255, 0.22)"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
        </g>

        <path
          d="M110 96 C144 96 162 122 162 150 C162 176 140 190 110 190 C80 190 58 176 58 150 C58 122 76 96 110 96 Z"
          fill="url(#agentsBeaverBody)"
        />
        <ellipse
          cx="110"
          cy="158"
          rx="31"
          ry="27"
          fill="rgba(255, 255, 255, 0.16)"
        />

        <circle
          cx="70"
          cy="68"
          r="11"
          fill="#6B4630"
        />
        <circle
          cx="150"
          cy="68"
          r="11"
          fill="#6B4630"
        />

        <circle
          cx="110"
          cy="86"
          r="42"
          fill="url(#agentsBeaverBody)"
        />

        <ellipse
          cx="110"
          cy="104"
          rx="27"
          ry="21"
          fill="url(#agentsMuzzle)"
        />
        <ellipse
          cx="110"
          cy="94"
          rx="7"
          ry="5"
          fill="#462853"
        />
        <rect
          x="101"
          y="110"
          width="8"
          height="19"
          rx="2"
          fill="#FFFFFF"
        />
        <rect
          x="111"
          y="110"
          width="8"
          height="19"
          rx="2"
          fill="#FFFFFF"
        />

        <g className={eye}>
          <circle
            cx="94"
            cy="74"
            r="9"
            fill="#FFFFFF"
          />
          <circle
            cx="96"
            cy="75"
            r="5"
            fill="#0C1436"
          />
          <circle
            cx="93"
            cy="72"
            r="1.8"
            fill="#FFFFFF"
          />
        </g>
        <g className={`${eye} ${eyeEnd}`}>
          <circle
            cx="126"
            cy="74"
            r="9"
            fill="#FFFFFF"
          />
          <circle
            cx="124"
            cy="75"
            r="5"
            fill="#0C1436"
          />
          <circle
            cx="121"
            cy="72"
            r="1.8"
            fill="#FFFFFF"
          />
        </g>

        <path
          d="M68 58 C68 30 87 14 110 14 C133 14 152 30 152 58 Z"
          fill="url(#agentsHardHat)"
        />
        <path
          d="M110 14 L110 58"
          stroke="rgba(92, 58, 34, 0.25)"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <ellipse
          cx="110"
          cy="58"
          rx="50"
          ry="9"
          fill="url(#agentsHardHat)"
        />
        <ellipse
          cx="110"
          cy="56"
          rx="50"
          ry="7"
          fill="rgba(255, 255, 255, 0.22)"
        />

        <ellipse
          cx="64"
          cy="152"
          rx="12"
          ry="10"
          fill="#6B4630"
        />

        <rect
          x="150"
          y="168"
          width="64"
          height="15"
          rx="4"
          fill="#A9702F"
        />
        <path
          d="M158 173 L206 173 M158 178 L198 178"
          stroke="rgba(92, 58, 34, 0.35)"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
        />

        <g className={hammer}>
          <rect
            x="153"
            y="110"
            width="9"
            height="44"
            rx="4"
            fill="#5C3A22"
          />
          <rect
            x="140"
            y="97"
            width="35"
            height="16"
            rx="5"
            fill="#8F93A3"
          />
          <rect
            x="140"
            y="97"
            width="35"
            height="6"
            rx="3"
            fill="rgba(255, 255, 255, 0.35)"
          />
        </g>
        <ellipse
          cx="157"
          cy="150"
          rx="12"
          ry="10"
          fill="#6B4630"
        />

        <g className={impact}>
          <path
            d="M200 152 L204 140 M200 152 L214 148 M200 152 L208 162"
            stroke="#FF9903"
            strokeWidth="4"
            strokeLinecap="round"
            fill="none"
          />
        </g>
      </g>
    </svg>
  )
}
